import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/shared/services/query-keys';
import { useUIStore } from '@/shared/stores/ui-store';
import { getApiErrorMessage } from '@/shared/utils/api-error-message';
import {
  createCampaign,
  updateCampaign,
  deleteCampaign,
  duplicateCampaign,
  submitCampaign,
  submitCampaignForReview,
  pauseCampaign,
  resumeCampaign,
  saveCampaignDraft,
  updateCampaignDraft,
  launchCampaign,
  syncCampaignStatus,
  validateCampaignLanding,
  fetchBudgetSuggestion,
  fetchCopySuggestion,
  fetchAudienceEstimate,
  setDefaultPage,
  uploadCreativeImage,
  uploadCreativeVideo,
  updateCampaignCreative,
} from './campaigns-api';
import type { UpdateCampaignPayload, UpdateCreativePayload } from './campaigns-api';
import type { Campaign } from '@/shared/types/entities';
import type { CampaignWizardFormData, AudienceEstimateRequest } from '../types/campaigns.types';

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: (payload: Partial<Campaign>) => createCampaign(payload),
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all() });
      addToast({ message: 'Campaign created', variant: 'success' });
    },
    onError: () => {
      addToast({ message: 'Failed to create campaign', variant: 'error' });
    },
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCampaignPayload }) =>
      updateCampaign(id, payload),
    retry: 0,
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.lists() });
      addToast({ message: 'Campaign updated', variant: 'success' });
    },
    onError: () => {
      addToast({ message: 'Failed to update campaign', variant: 'error' });
    },
  });
}

/**
 * Edit a creative on an existing campaign. The backend only accepts this while the
 * campaign is DRAFT or PAUSED and answers 400 otherwise, so surface its message.
 */
export function useUpdateCreative() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: (variables: {
      campaignId: string;
      creativeId: string;
      payload: UpdateCreativePayload;
    }) => updateCampaignCreative(variables),
    retry: 0,
    onSuccess: (_data, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.detail(campaignId) });
      addToast({ message: 'Creative updated', variant: 'success' });
    },
    onError: (error) => {
      addToast({
        message: getApiErrorMessage(error, 'Failed to update creative'),
        variant: 'error',
      });
    },
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: deleteCampaign,
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all() });
      addToast({ message: 'Campaign deleted', variant: 'success' });
    },
    onError: () => {
      addToast({ message: 'Failed to delete campaign', variant: 'error' });
    },
  });
}

export function useArchiveCampaign() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: deleteCampaign,
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all() });
      addToast({ message: 'Campaign archived', variant: 'success' });
    },
    onError: () => {
      addToast({ message: 'Failed to archive campaign', variant: 'error' });
    },
  });
}

export function useDuplicateCampaign() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: duplicateCampaign,
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all() });
      addToast({ message: 'Campaign duplicated', variant: 'success' });
    },
    onError: () => {
      addToast({ message: 'Failed to duplicate campaign', variant: 'error' });
    },
  });
}

export function useSubmitForReview() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: submitCampaignForReview,
    retry: 0,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.lists() });
      addToast({ message: 'Campaign submitted for review', variant: 'success' });
    },
    onError: () => {
      addToast({ message: 'Failed to submit campaign for review', variant: 'error' });
    },
  });
}

export function useSubmitCampaign() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: submitCampaign,
    retry: 0,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.detail(id) });
      addToast({ message: 'Campaign submitted', variant: 'success' });
    },
    onError: (error) => {
      addToast({
        message: getApiErrorMessage(error, 'Failed to submit campaign'),
        variant: 'error',
      });
    },
  });
}

export function usePauseCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: pauseCampaign,
    retry: 0,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.lists() });
    },
  });
}

export function useResumeCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resumeCampaign,
    retry: 0,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.lists() });
    },
  });
}

/**
 * Validate a landing URL without launching. Used on-blur in the wizard so
 * the user sees HTTPS / hostname errors before clicking Launch.
 * Returns the validation result rather than throwing — the form binds
 * the error list directly.
 */
export function useValidateCampaignLanding() {
  return useMutation({
    mutationFn: validateCampaignLanding,
    retry: 0,
  });
}

/**
 * Force an ad-platform status sync for a single campaign.
 * Emits a toast when the sync results in a state change so the user
 * understands the "Sync now" button did something.
 */
export function useSyncCampaignStatus() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: syncCampaignStatus,
    retry: 0,
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.lists() });
      if (data.changed) {
        addToast({
          message: `Status updated to ${data.status}`,
          variant: 'success',
        });
      } else {
        addToast({
          message: 'Status is already up to date',
          variant: 'info',
        });
      }
    },
    onError: () => {
      addToast({
        message: 'Could not reach the ad platform. Try again in a moment.',
        variant: 'error',
      });
    },
  });
}

// ---- Wizard mutations ----

export function useSaveDraft() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: (payload: Partial<CampaignWizardFormData>) => saveCampaignDraft(payload),
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all() });
      addToast({ message: 'Draft saved', variant: 'success' });
    },
    onError: () => {
      addToast({ message: 'Failed to save draft', variant: 'error' });
    },
  });
}

export function useUpdateDraft() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CampaignWizardFormData> }) =>
      updateCampaignDraft(id, payload),
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all() });
      addToast({ message: 'Draft updated', variant: 'success' });
    },
    onError: () => {
      addToast({ message: 'Failed to update draft', variant: 'error' });
    },
  });
}

export function useLaunchCampaign() {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: ({
      payload,
      supersededDraftId,
    }: {
      payload: CampaignWizardFormData;
      supersededDraftId?: string | null;
    }) => launchCampaign(payload, supersededDraftId),
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all() });
      addToast({ message: 'Campaign launched successfully!', variant: 'success' });
    },
    onError: (error) => {
      addToast({
        message: getApiErrorMessage(error, 'Failed to launch campaign'),
        variant: 'error',
      });
    },
  });
}

export function useBudgetSuggestion() {
  return useMutation({
    mutationFn: (params: {
      objective: string;
      platform: string;
      durationDays?: number;
      audienceSize?: number;
      eventDate?: string;
    }) => fetchBudgetSuggestion(params),
    retry: 0,
  });
}

export function useCopySuggestion() {
  return useMutation({
    mutationFn: (params: {
      platform: string;
      objective?: string;
      eventName: string;
      eventDescription?: string;
      eventCategory?: string;
      location?: string;
      destinationUrl?: string;
      tone?: string;
      callToAction?: string;
      targetAudience?: string[];
      variations?: number;
    }) => fetchCopySuggestion(params),
    retry: 0,
  });
}

/**
 * Generates an audience-size estimate on demand. Both Meta and Google
 * resolve to a real platform call server-side and require a connected ad
 * account — there is no local approximation, so a missing connection or a
 * platform-side failure surfaces as an error via `onError`. A mutation (not
 * a query) because the estimate is only ever produced by an explicit
 * "Generate" click, never auto-fetched on targeting changes.
 */
export function useGenerateAudienceEstimate() {
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: (config: AudienceEstimateRequest) => fetchAudienceEstimate(config),
    retry: 0,
    onError: (error) => {
      addToast({
        message: getApiErrorMessage(error, 'Could not generate an audience estimate'),
        variant: 'error',
      });
    },
  });
}

/**
 * Persists the Facebook Page the wizard picked as the default for an ad
 * account. The launch flow reads `adAccount.defaultPageId` to populate
 * `object_story_spec.page_id` on Meta — without this, Meta rejects every
 * Ad with "Selecione uma Página".
 */
export function useSetDefaultPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ adAccountId, pageId }: { adAccountId: string; pageId: string }) =>
      setDefaultPage(adAccountId, pageId),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.metaPages(vars.adAccountId),
      });
    },
    retry: 0,
  });
}

/**
 * Upload a user-picked creative image to S3 so the wizard can reference it
 * by storage key on launch. The BE returns the same shape as AI-generated
 * images, which lets the wizard merge both flows into a single list.
 */
export function useUploadCreativeImage() {
  const addToast = useUIStore((s) => s.addToast);
  return useMutation({
    mutationFn: uploadCreativeImage,
    onError: (err) => {
      addToast({
        message: `Couldn't upload image: ${err instanceof Error ? err.message : 'Unknown error'}`,
        variant: 'error',
      });
    },
    retry: 0,
  });
}

/**
 * Upload a user-picked creative video straight to S3 (presigned PUT). Returns
 * the storage key the wizard sends on the creative at launch.
 */
export function useUploadCreativeVideo() {
  const addToast = useUIStore((s) => s.addToast);
  return useMutation({
    mutationFn: uploadCreativeVideo,
    onError: (err) => {
      addToast({
        message: `Couldn't upload video: ${err instanceof Error ? err.message : 'Unknown error'}`,
        variant: 'error',
      });
    },
    retry: 0,
  });
}
